/**
 * Tenancy domain API functions (Step 7.1.2).
 *
 * Tenancy CRUD, tenancy revenue CRUD, paginated tenancy list.
 * Extracted from adminData/index.js for team scalability.
 */
import {
  API_BASE_URL,
  getApiHeaders,
  expectPaginatedItems,
  parseAdminErrorResponse,
} from "./core";

/** Shared query-string builder for GET /api/admin/tenancies. */
function tenancyListUrl(params) {
  const sp = new URLSearchParams();
  if (params.room_id) sp.set("room_id", params.room_id);
  if (params.unit_id) sp.set("unit_id", params.unit_id);
  if (params.tenant_id) sp.set("tenant_id", params.tenant_id);
  if (params.include_participant) sp.set("include_participant", "true");
  if (params.status) sp.set("status", params.status);
  if (params.include_cancelled === true) sp.set("include_cancelled", "true");
  if (params.search) sp.set("search", params.search);
  if (params.sort) sp.set("sort", params.sort);
  if (params.limit != null) sp.set("limit", String(params.limit));
  if (params.skip != null) sp.set("skip", String(params.skip));
  const qs = sp.toString();
  return `${API_BASE_URL}/api/admin/tenancies${qs ? `?${qs}` : ""}`;
}

/**
 * GET /api/admin/tenancies — returns the ITEM ARRAY, not the envelope.
 *
 * ``expectPaginatedItems`` unwraps ``{items, total}`` down to ``items``.
 * Every historical caller (unit/tenant detail pages, the tenancy step of
 * the invoice wizard, ``fetchAdminTenanciesAll``'s ``Array.isArray``
 * loop guard) depends on that array, so the contract stays as is.
 * Callers that need ``total`` — i.e. anything paging — must use
 * ``fetchAdminTenanciesPage`` below.
 *
 * ``search`` (C2) matches reference number, tenant name/e-mail, unit
 * title/short id and room name, case-insensitively; an empty value is
 * treated as no search. ``sort="picker_relevance"`` orders active →
 * reserved → ended → cancelled, newest move-in first inside each
 * bucket. Omitting ``sort`` keeps the historical move_in_date DESC.
 */
export function fetchAdminTenancies(params = {}) {
  return fetch(tenancyListUrl(params), { headers: getApiHeaders() })
    .then((res) => {
      if (!res.ok) throw new Error("Tenancies konnten nicht geladen werden.");
      return res.json();
    })
    .then((data) => expectPaginatedItems(data, "GET /api/admin/tenancies"));
}

/**
 * GET /api/admin/tenancies — returns the FULL ``{items, total}`` envelope.
 *
 * Same endpoint and same parameters as ``fetchAdminTenancies``; the only
 * difference is that the envelope survives. ``AsyncCombobox`` needs both
 * halves: ``items`` to render the page and ``total`` to decide whether a
 * "Mehr laden" affordance belongs there — reading ``.items`` off a bare
 * array yields ``undefined``, which the base then renders as an empty
 * result set. That is exactly the C2 regression this function fixes;
 * mirrors ``fetchAdminTasks``, which already returns the envelope.
 */
export function fetchAdminTenanciesPage(params = {}) {
  return fetch(tenancyListUrl(params), { headers: getApiHeaders() }).then(
    async (res) => {
      if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
      const data = await res.json();
      const items = expectPaginatedItems(data, "GET /api/admin/tenancies");
      return {
        items,
        total: typeof data?.total === "number" ? data.total : items.length,
      };
    }
  );
}

/**
 * GET /api/admin/tenancies/{tenancyId}
 *
 * One tenancy in the SAME enriched shape the list returns. Used by the
 * work-report tenancy picker to hydrate an already-linked tenancy that
 * may be ended, cancelled, or outside the first search page.
 */
export function fetchAdminTenancyById(tenancyId) {
  return fetch(`${API_BASE_URL}/api/admin/tenancies/${tenancyId}`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * Fetch all tenancies for the org (pages through limit=200 until exhausted).
 */
export async function fetchAdminTenanciesAll(params = {}) {
  const limit = 200;
  let skip = 0;
  const all = [];
  for (;;) {
    const batch = await fetchAdminTenancies({ ...params, limit, skip });
    if (!Array.isArray(batch) || batch.length === 0) break;
    all.push(...batch);
    if (batch.length < limit) break;
    skip += limit;
  }
  return all;
}

/**
 * GET /api/admin/tenancies/summary — org-wide tenancy figures, two questions.
 *
 * Returns `{ total, running, display_status_counts: { active, reserved,
 * notice_given, ended, cancelled } }`, all five status keys always present.
 *
 * `display_status_counts` answers WHAT STATE each tenancy is in — the same
 * `display_status` every row of the list renders as its badge. Four of the
 * five buckets are also exactly what `fetchAdminTenanciesPage({status})`
 * returns for that word.
 *
 * `active` IS THE EXCEPTION, and it is the one a card is most likely to want.
 * The `active` FILTER returns the contracts RUNNING today — including a
 * terminated one whose effective end is still ahead, which the `active` BUCKET
 * excludes because its badge reads `notice_given`. A card pairing the bucket
 * with that filter shows a smaller number above a longer list. Use `running`.
 *
 * `running` is computed server-side by `tenancy_lifecycle.tenancy_is_running_on`
 * and is not a sixth bucket. It is also not `active + notice_given`: a
 * termination whose effective date has passed with no move-out ever recorded
 * still derives `notice_given`, and it is not running. Callers must use this
 * field as-is and never sum buckets themselves — since `running` and the
 * `notice_given` bucket overlap, the counts do not partition `total`.
 */
export function fetchAdminTenancyStatusSummary() {
  return fetch(`${API_BASE_URL}/api/admin/tenancies/summary`, {
    headers: getApiHeaders(),
  }).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * Create a tenancy (POST /api/admin/tenancies). Optional `participants` for multi-person contracts.
 */
export async function createAdminTenancy(body) {
  const res = await fetch(`${API_BASE_URL}/api/admin/tenancies`, {
    method: "POST",
    headers: getApiHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export async function patchAdminTenancy(tenancyId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/**
 * POST /api/admin/tenancies/{id}/cancel
 *
 * Soft-cancels a tenancy. Cancelability is derived server-side from the
 * lifecycle + dates (derive_tenancy_cancelability) — the raw active/reserved
 * status is NOT authoritative:
 *   - before move-in: normally cancellable.
 *   - within the 3-day no-show grace (move-in .. move-in+2): only with
 *     no_show_confirmed=true, else 422.
 *   - after the grace, or already ended/terminated: 409 (use the
 *     termination/exit flow).
 * Reason is trimmed server-side; min_length=3, max_length=500. Pass
 * no_show_confirmed only when the response's cancel_requires_no_show_confirmation
 * is true (the caller reads it from the serialized tenancy).
 */
export async function cancelAdminTenancy(
  tenancyId,
  cancellation_reason,
  no_show_confirmed = false
) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/cancel`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({ cancellation_reason, no_show_confirmed }),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/**
 * GET /api/admin/tenancies/{id}/cancellation-preflight
 *
 * The facts an admin needs BEFORE cancelling a tenancy: the authoritative
 * lifecycle cancelability, every local invoice with its stored AND effective
 * status, the linked Bexio artefacts, move-in evidence and a contract count.
 *
 * Strictly read-only — it cancels nothing, touches no invoice, calls Bexio not
 * at all and writes no audit entry. It is a REVIEW aid, never a security
 * boundary: `cancelAdminTenancy` re-derives cancelability server-side on the
 * row it is about to write, so a preflight that has gone stale between the two
 * calls loses to the server, as it should.
 *
 * 404 (never 403) for an unknown or cross-org tenancy.
 */
export function fetchAdminTenancyCancellationPreflight(tenancyId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(
      tenancyId
    )}/cancellation-preflight`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * POST /api/admin/tenancies/{id}/reactivate
 *
 * Reactivates a cancelled tenancy ("Stornierung aufheben"). Backend
 * transition rule: only cancelled -> active|reserved allowed; any other
 * source status returns 409. Target status is derived server-side from
 * move_in_date (future -> reserved, today/past -> active). No request body.
 */
export async function reactivateAdminTenancy(tenancyId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/reactivate`,
    {
      method: "POST",
      headers: getApiHeaders(),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export function fetchAdminTenancyRevenue(tenancyId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/revenue`,
    { headers: getApiHeaders() }
  ).then((res) => {
    if (!res.ok) throw new Error("Einnahmen konnten nicht geladen werden.");
    return res.json();
  });
}

export async function createAdminTenancyRevenue(tenancyId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/revenue`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export async function patchAdminTenancyRevenue(revenueId, body) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancy-revenue/${encodeURIComponent(revenueId)}`,
    {
      method: "PATCH",
      headers: getApiHeaders(),
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/**
 * GET /api/admin/tenancies/{id}/notes — internal CRM notes on ONE tenancy.
 *
 * NOT `fetchAdminTenantNotes`. Those are bound to a PERSON, and a tenant can
 * hold several tenancies at once and over time, so a note kept on the person
 * cannot say which contract it belongs to. `tenancy_notes` (migration 169)
 * records the relation at write time; these three functions are the only way
 * this workspace reads or writes it.
 *
 * @returns {Promise<{ items: Array<object> }>} newest first, server-ordered.
 */
export function fetchAdminTenancyNotes(tenancyId) {
  return fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/notes`,
    { headers: getApiHeaders() }
  ).then(async (res) => {
    if (!res.ok) throw new Error(await parseAdminErrorResponse(res));
    return res.json();
  });
}

/**
 * POST /api/admin/tenancies/{id}/notes — returns the created note itself,
 * not an envelope. `content` is the only field the backend accepts.
 */
export async function createAdminTenancyNote(tenancyId, content) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(tenancyId)}/notes`,
    {
      method: "POST",
      headers: getApiHeaders(),
      body: JSON.stringify({ content }),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

/**
 * PUT /api/admin/tenancies/{id}/notes/{note_id} — returns the updated note.
 *
 * The tenancy id stays in the path: the backend 404s unless the note, the
 * tenancy and the organization all match, so a note is never addressed by id
 * alone. There is deliberately no DELETE — no notes domain in this product
 * has one.
 */
export async function updateAdminTenancyNote(tenancyId, noteId, content) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(
      tenancyId
    )}/notes/${encodeURIComponent(noteId)}`,
    {
      method: "PUT",
      headers: getApiHeaders(),
      body: JSON.stringify({ content }),
    }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  return res.json();
}

export async function deleteAdminTenancyRevenue(revenueId) {
  const res = await fetch(
    `${API_BASE_URL}/api/admin/tenancy-revenue/${encodeURIComponent(revenueId)}`,
    { method: "DELETE", headers: getApiHeaders() }
  );
  if (!res.ok) {
    throw new Error(await parseAdminErrorResponse(res));
  }
  try {
    return await res.json();
  } catch {
    return { status: "ok" };
  }
}
